import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getOopsRedirectReason, getWelcomeClient } from '../services/applicationApi';
import { isValidClientGuidFormat } from '../utils/sessionIds';
import '../styles/theme.css';
import '../styles/WelcomePage.css';

function digitsForTel(phone: string): string {
  return phone.replace(/\D/g, '');
}

function clientGuidFromSearch(searchParams: URLSearchParams): string {
  return searchParams.get('ClientId')?.trim() ?? '';
}

const WelcomePage = (): JSX.Element | null => {
  const location = useLocation();
  const navigate = useNavigate();
  const { clientGuid: clientGuidParam } = useParams();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();

  const resolvedGuid = useMemo(() => {
    const fromPath = clientGuidParam?.trim();
    if (fromPath) {
      return isValidClientGuidFormat(fromPath) ? fromPath : null;
    }
    const q = clientGuidFromSearch(searchParams);
    if (q && isValidClientGuidFormat(q)) return q;
    return null;
  }, [clientGuidParam, searchParams]);

  const [welcomeClient, setWelcomeClient] = useState<{
    fullName: string;
    phone: string | null;
  } | null>(null);
  const [loadingClient, setLoadingClient] = useState(false);

  /** Declarative redirect avoids rendering `null` (white flash) before imperative navigate runs. */
  const oopsRedirectReason = useMemo((): 'client_not_found' | null => {
    const fromPath = clientGuidParam?.trim() ?? '';
    const qOnly = clientGuidFromSearch(searchParams);
    if (fromPath && !isValidClientGuidFormat(fromPath)) return 'client_not_found';
    if (qOnly && !fromPath && !isValidClientGuidFormat(qOnly)) return 'client_not_found';
    if (!resolvedGuid) return 'client_not_found';
    return null;
  }, [clientGuidParam, resolvedGuid, searchParams]);

  useEffect(() => {
    if (!resolvedGuid) {
      setWelcomeClient(null);
      setLoadingClient(false);
      return;
    }

    let cancelled = false;
    setLoadingClient(true);
    void getWelcomeClient(resolvedGuid)
      .then((data) => {
        if (cancelled) return;
        setWelcomeClient({
          fullName: data.fullName.trim(),
          phone: data.phone,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const reason = getOopsRedirectReason(err);
        if (reason) {
          navigate('/oops', { state: { reason }, replace: true });
          return;
        }
        navigate('/oops', { state: { reason: 'generic' }, replace: true });
      })
      .finally(() => {
        if (!cancelled) setLoadingClient(false);
      });

    return () => {
      cancelled = true;
    };
  }, [resolvedGuid, navigate]);

  /** Welcome is English-only; sync i18n so persisted `es` does not mismatch the sole select option or leave Spanish copy. */
  useEffect(() => {
    void i18n.changeLanguage('en');
  }, [i18n]);

  if (oopsRedirectReason !== null) {
    return <Navigate to="/oops" replace state={{ reason: oopsRedirectReason }} />;
  }

  const displayName = welcomeClient?.fullName?.trim() ?? '';
  const clientFullName =
    displayName.length > 0 ? displayName : t('welcome.defaultClientName');
  const displayPhone = welcomeClient?.phone?.trim() ?? '';
  const hasPhone = displayPhone.length > 0;

  const showContent = Boolean(resolvedGuid && welcomeClient && !loadingClient);

  return (
    <div className="welcome-page">
      <header className="welcome-header">
        <img
          src={`${process.env.PUBLIC_URL}/aspirion-logo.png`}
          alt={t('welcome.logoAlt')}
          className="welcome-logo"
        />
      </header>

      <div className="welcome-card">
        <div className="welcome-banner" />
        <div className="welcome-banner-image-wrap">
          <img
            src={`${process.env.PUBLIC_URL}/aspirion-welcome.svg`}
            alt=""
            className="welcome-banner-image"
            aria-hidden
          />
        </div>
        <main className="welcome-content">
          <h1 className="welcome-heading">{t('welcome.heading')}</h1>

          {loadingClient ? (
            <p className="welcome-text">{t('welcome.loadingClient')}</p>
          ) : showContent ? (
            <>
              <p className="welcome-text">
                {t('welcome.paragraph1Dynamic', { clientFullName })}
              </p>
              {hasPhone ? (
                <p className="welcome-text">
                  {t('welcome.paragraph2Prefix')}{' '}
                  <a
                    href={`tel:${digitsForTel(displayPhone)}`}
                    className="welcome-phone-link"
                  >
                    {displayPhone}
                  </a>
                </p>
              ) : (
                <p className="welcome-text">{t('welcome.paragraph2NoPhone')}</p>
              )}
            </>
          ) : null}

          <div className="welcome-language-field">
            <label htmlFor="welcome-language" className="welcome-language-label">
              {t('welcome.languageLabel')}
            </label>
            <select
              id="welcome-language"
              className="welcome-language-select"
              value="en"
              onChange={(e) => {
                void i18n.changeLanguage(e.target.value);
              }}
              aria-label={t('welcome.languageAria')}
            >
              <option value="en">{t('languages.en')}</option>
            </select>
          </div>

          <Link
            to={{ pathname: '/applications', search: location.search }}
            className="welcome-start-button"
          >
            {t('welcome.startCta')}
          </Link>
        </main>
      </div>
    </div>
  );
};

export default WelcomePage;
